/**
 * useOrderAlerts — the ONE order-alert engine for every dashboard
 * ─────────────────────────────────────────────────────────────────────────────
 * Replaces the six near-duplicate, subtly-divergent copies of "make an
 * AudioContext + useSound + beep on a socket event" that lived in Dashboard.js,
 * SellerDashboard.js, RestaurantVendorDashboard.js, HealthcareVendorDashboard.js,
 * ThreePL/PartnerDashboard.js and ThreePL/PartnerOrders.js. One module ⇒ one
 * consistent, reliable behaviour.
 *
 * What it does that the old per-file code did NOT:
 *   • Remembers the enabled state (localStorage) AND auto-unlocks the audio on the
 *     first user gesture after a reload, so operators don't have to re-click
 *     "Enable" every session.
 *   • Falls back to an OS-level Notification (via the Notification API) when the
 *     tab is hidden/backgrounded — a bridge to full Web Push even before the SW
 *     push lands.
 *   • Optional "repeat until acknowledged" so a new order keeps chiming until an
 *     operator actually looks.
 *   • Opportunistically registers this browser for server Web Push, so alerts
 *     keep coming even with the tab closed.
 *
 * The hook is deliberately UI-agnostic: it returns state + actions; each
 * dashboard renders its own toggle and calls `notify()` from its own socket
 * handler. It does NOT open sockets — every dashboard already owns its socket.
 * ───────────────────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  isPushSupported,
  getPermission,
  subscribeToWebPush,
  unsubscribeFromWebPush,
  getSubscriptionState,
  WEB_PUSH_STATE_EVENT,
} from "../utilities/webPush";
import { resolveToneUrl } from "../utilities/tones";
import {
  fetchNotificationClientConfig,
  invalidateNotificationClientConfig,
} from "../utilities/notificationClientConfig";
import {
  ORDER_ALERTS_CONFIG_EVENT,
  ORDER_ALERTS_ENABLED_KEY,
  ORDER_ALERTS_STATE_EVENT,
  readOrderAlertsEnabled,
  writeOrderAlertsEnabled,
} from "../utilities/notificationState";

// Shared, session-cached fetch of the team's sound config (tone/volume/repeat),
// so all six dashboards issue at most ONE request. Rare to change; a reload picks
// up new settings.
// The client config is shared with the other consumers of /webpush/client-config
// (see utilities/notificationClientConfig.js) so the session makes ONE request.
const fetchClientConfig = fetchNotificationClientConfig;

/**
 * @param {object}  opts
 * @param {string}  opts.soundUrl              imported mp3 to play
 * @param {number}  [opts.volume=0.5]
 * @param {number}  [opts.playbackRate=0.95]
 * @param {boolean} [opts.repeatUntilAck=false] loop the beep until acknowledge()
 * @param {number}  [opts.repeatIntervalMs=8000]
 * @param {number}  [opts.maxRepeats=8]
 * @param {string}  [opts.desktopTitle="New order"]
 */
export default function useOrderAlerts({
  soundUrl,
  volume = 0.5,
  playbackRate = 0.95,
  repeatUntilAck = false,
  repeatIntervalMs = 8000,
  maxRepeats = 8,
  desktopTitle = "New order received",
} = {}) {
  const [enabled, setEnabled] = useState(readOrderAlertsEnabled());
  const [permission, setPermission] = useState(getPermission());
  const [pushState, setPushState] = useState({ supported: isPushSupported(), permission, subscribed: false });

  // True once the browser has REJECTED a play attempt. Distinct from `enabled`,
  // which is only a persisted preference in localStorage — and the gap between
  // the two is exactly the bug this exists to expose: the dashboard could read
  // "Notifications On" while every play() was being refused by the autoplay
  // policy and the rejection swallowed by an empty catch.
  const [audioBlocked, setAudioBlocked] = useState(false);

  const audioCtxRef = useRef(null);
  const audioElRef = useRef(null);
  const repeatTimerRef = useRef(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // Set once a real user gesture has primed an element. Tracked because the
  // primed element is thrown away whenever the tone URL changes, and a stale
  // "we already unlocked" assumption is what left the page silent.
  const unlockedRef = useRef(false);
  const armGestureUnlockRef = useRef(null);

  // The team-selected tone + volume + repeat cfg (from /webpush/client-config) win
  // over the per-dashboard defaults. Held in refs so a config update never forces
  // the long-lived socket effects to re-run.
  const soundUrlRef = useRef(soundUrl);
  const volumeRef = useRef(volume);
  const rateRef = useRef(playbackRate);
  const repeatCfgRef = useRef({ repeatUntilAck, repeatIntervalMs, maxRepeats });

  // ── Lazy audio element (one per hook; cloned per play so beeps can overlap) ──
  const getAudioEl = useCallback(() => {
    if (!audioElRef.current && soundUrlRef.current) {
      const el = new Audio(soundUrlRef.current);
      el.preload = "auto";
      el.volume = Math.max(0, Math.min(1, volumeRef.current));
      audioElRef.current = el;
    }
    return audioElRef.current;
  }, []);

  // ── Unlock the AudioContext (must run inside a user gesture) ─────────────────
  const unlockAudio = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) audioCtxRef.current = new Ctx();
      }
      if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
        audioCtxRef.current.resume().catch(() => {});
      }
      // Prime the element so the first real alert isn't blocked by autoplay.
      const el = getAudioEl();
      if (el) {
        el.muted = true;
        el.play()
          .then(() => {
            el.pause();
            el.currentTime = 0;
            el.muted = false;
            // Priming succeeded inside a gesture, so this document is now
            // allowed to play audio. Remember which element was primed.
            unlockedRef.current = true;
            setAudioBlocked(false);
          })
          .catch(() => {
            el.muted = false;
          });
      }
    } catch (_e) {
      /* ignore */
    }
  }, [getAudioEl]);

  // ── Play the beep once ───────────────────────────────────────────────────────
  const play = useCallback(() => {
    const base = getAudioEl();
    if (!base) return;
    try {
      // Clone so rapid consecutive orders don't cut each other off.
      const node = base.cloneNode(true);
      node.muted = false; // defensive: the primed base may still be muted mid-unlock
      node.volume = Math.max(0, Math.min(1, volumeRef.current));
      node.playbackRate = rateRef.current;
      const p = node.play();
      if (p && typeof p.then === "function") {
        p.then(() => setAudioBlocked(false)).catch((err) => {
          // Autoplay blocked. The old code only retried when the AudioContext
          // happened to be "suspended" — if the context was null (which it is
          // whenever no gesture ever primed it) the catch did nothing at all,
          // and nothing anywhere logged or surfaced it. That is precisely how a
          // dashboard reading "Notifications On" stayed silent.
          const retry = () =>
            node.play().then(
              () => setAudioBlocked(false),
              () => {
                setAudioBlocked(true);
                // eslint-disable-next-line no-console
                console.warn(
                  "[orderAlerts] the browser blocked the alert sound; " +
                    "a click or keypress on the page is needed to allow it.",
                  err && err.name
                );
                // Re-arm the gesture listeners so the NEXT interaction fixes it
                // instead of leaving this tab permanently mute.
                unlockedRef.current = false;
                if (armGestureUnlockRef.current) armGestureUnlockRef.current();
              }
            );

          if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
            audioCtxRef.current.resume().then(retry, retry);
          } else {
            retry();
          }
        });
      }
    } catch (_e) {
      /* ignore */
    }
  }, [getAudioEl]);

  // Held in a ref so applyClientConfig (defined inside a mount-once effect) can
  // re-prime without taking unlockAudio as a dependency and re-running the
  // whole effect on every render.
  const unlockAudioRef = useRef(unlockAudio);
  unlockAudioRef.current = unlockAudio;

  const stopRepeat = useCallback(() => {
    if (repeatTimerRef.current) {
      clearInterval(repeatTimerRef.current);
      repeatTimerRef.current = null;
    }
  }, []);

  /** Acknowledge the current alert (stops any repeat loop). */
  const acknowledge = useCallback(() => stopRepeat(), [stopRepeat]);

  // ── The main entry point dashboards call on a new-order event ────────────────
  const notify = useCallback(
    (payload = {}) => {
      if (!enabledRef.current) return;

      play();

      // Desktop Notification when the tab isn't focused (bridge before full push).
      try {
        if (typeof document !== "undefined" && document.hidden && getPermission() === "granted") {
          const n = new Notification(payload.title || desktopTitle, {
            body: payload.body || "A new order just arrived. Click to view.",
            icon: "/logo192.png",
            tag: payload.tag || "lusuku-order",
            renotify: true,
          });
          n.onclick = () => {
            window.focus();
            if (payload.url) window.location.assign(payload.url);
            n.close();
          };
        }
      } catch (_e) {
        /* Notification unsupported — sound already played */
      }

      // Repeat-until-acknowledged (config resolved from team settings).
      const cfg = repeatCfgRef.current;
      if (cfg.repeatUntilAck) {
        stopRepeat();
        let count = 0;
        repeatTimerRef.current = setInterval(() => {
          count += 1;
          if (count >= cfg.maxRepeats || !enabledRef.current) {
            stopRepeat();
            return;
          }
          play();
        }, cfg.repeatIntervalMs);
      }
    },
    [play, desktopTitle, stopRepeat]
  );

  // ── Enable / disable ─────────────────────────────────────────────────────────
  const permissionRequestRef = useRef(null);
  const enable = useCallback(({ silent = false, requestPush = true } = {}) => {
    // Sound is an independent preference. A pending/blocked browser prompt or
    // unavailable Web Push must never leave the dashboard toggle stuck Off.
    enabledRef.current = true;
    setEnabled(true);
    writeOrderAlertsEnabled(true);
    unlockAudio();
    if (!silent) play(); // stay inside the click gesture, without a timer/await

    if (!requestPush || permissionRequestRef.current) return;
    const enroll = async () => {
      try {
        if (getPermission() === "default" && typeof Notification !== "undefined") {
          setPermission(await Notification.requestPermission());
        }
        // The operator may have turned sound off while the prompt was open.
        if (!enabledRef.current || !isPushSupported() || getPermission() !== "granted") return;
        const result = await subscribeToWebPush();
        if (!enabledRef.current) {
          if (result.ok) await unsubscribeFromWebPush();
          return;
        }
        setPushState(await getSubscriptionState());
      } catch (_error) {
        // Web Push is best-effort; sound remains enabled.
      }
    };
    permissionRequestRef.current = enroll().finally(() => {
      permissionRequestRef.current = null;
    });
  }, [unlockAudio, play]);

  const disable = useCallback(async ({ unsubscribePush = true } = {}) => {
    stopRepeat();
    enabledRef.current = false;
    setEnabled(false);
    writeOrderAlertsEnabled(false);
    if (unsubscribePush && isPushSupported()) {
      await unsubscribeFromWebPush();
      const nextState = await getSubscriptionState();
      setPushState(nextState);
    }
  }, [stopRepeat]);

  const toggle = useCallback(async () => {
    if (enabledRef.current) await disable();
    else await enable();
  }, [enable, disable]);

  // ── Explicit push controls (used by the Notifications screen too) ────────────
  const enablePush = useCallback(async () => {
    const r = await subscribeToWebPush();
    const st = await getSubscriptionState();
    setPushState(st);
    setPermission(getPermission());
    return r;
  }, []);

  const disablePush = useCallback(async () => {
    await unsubscribeFromWebPush();
    const st = await getSubscriptionState();
    setPushState(st);
    return { ok: true };
  }, []);

  // ── On mount: reflect real push state; if previously enabled, auto-unlock on
  //    the first user gesture so the beep works without re-clicking the toggle. ──
  useEffect(() => {
    let mounted = true;
    getSubscriptionState().then((st) => {
      if (!mounted) return;
      setPushState(st);
    });

    const applyClientConfig = (cfg) => {
      if (!mounted || !cfg?.sound) return;
      const s = cfg.sound;
      // A loop may have started before the async config arrived. Updating the
      // ref alone leaves that timer running with its captured old settings.
      if (s.repeatUntilAck === false) stopRepeat();
      if (typeof s.volume === "number") volumeRef.current = s.volume;
      if (typeof s.playbackRate === "number") rateRef.current = s.playbackRate;
      repeatCfgRef.current = {
        repeatUntilAck: s.repeatUntilAck ?? repeatCfgRef.current.repeatUntilAck,
        repeatIntervalMs: s.repeatIntervalMs ?? repeatCfgRef.current.repeatIntervalMs,
        maxRepeats: s.maxRepeats ?? repeatCfgRef.current.maxRepeats,
      };
      const url = resolveToneUrl(s.tone, s.customToneUrl, soundUrlRef.current);
      if (url && url !== soundUrlRef.current) {
        soundUrlRef.current = url;
        audioElRef.current = null; // force reload with the newly-selected tone
        // THE RACE THAT CAUSED THE SILENCE.
        //
        // This branch fires on essentially every mount: the per-dashboard
        // default is a webpack-hashed bundled mp3 while the server's default
        // tone is "classic" → /tones/classic.mp3, so the URLs always differ.
        // Discarding the element also discards whatever gesture-priming had
        // already happened to it — and since the unlock listeners were
        // registered `{ once: true }` AND removed each other, there was no
        // second chance for the life of the mount. The next real alert then
        // built a brand-new Audio() that had never played inside a gesture.
        //
        // Re-prime immediately if we were already unlocked (still inside the
        // permitted state, so this is allowed); otherwise re-arm the listeners
        // so the vendor's next click covers the new element.
        if (unlockedRef.current) {
          unlockAudioRef.current?.();
        } else if (armGestureUnlockRef.current) {
          armGestureUnlockRef.current();
        }
      } else if (audioElRef.current) {
        audioElRef.current.volume = Math.max(0, Math.min(1, volumeRef.current));
      }
    };

    // Pull the team's tone/volume/repeat config and let it override the defaults.
    fetchClientConfig().then(applyClientConfig);

    const refreshClientConfig = () => {
      invalidateNotificationClientConfig();
      fetchClientConfig().then(applyClientConfig);
    };
    window.addEventListener(ORDER_ALERTS_CONFIG_EVENT, refreshClientConfig);

    // Gesture unlock, re-armable. `armGestureUnlock` can be called again at any
    // time — when the tone changes, or after a play() rejection — so a tab is
    // never permanently mute just because the one-shot listener already fired
    // on an element that was subsequently thrown away.
    let onGesture = null;
    const disarmGestureUnlock = () => {
      if (!onGesture) return;
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      onGesture = null;
    };
    const armGestureUnlock = () => {
      if (!mounted || onGesture) return;
      onGesture = () => {
        disarmGestureUnlock();
        unlockAudio();
      };
      window.addEventListener("pointerdown", onGesture, { once: true });
      window.addEventListener("keydown", onGesture, { once: true });
    };
    armGestureUnlockRef.current = armGestureUnlock;
    if (readOrderAlertsEnabled()) armGestureUnlock();
    return () => {
      mounted = false;
      armGestureUnlockRef.current = null;
      disarmGestureUnlock();
      window.removeEventListener(
        ORDER_ALERTS_CONFIG_EVENT,
        refreshClientConfig
      );
      stopRepeat();
    };
  }, [unlockAudio, stopRepeat]);

  // Web Push is a delivery capability, not the operator's alert preference.
  // Subscription changes must never silently turn the dashboard toggle on/off.
  useEffect(() => {
    const syncPushState = (event) => setPushState(event.detail || {});

    window.addEventListener(WEB_PUSH_STATE_EVENT, syncPushState);
    return () =>
      window.removeEventListener(WEB_PUSH_STATE_EVENT, syncPushState);
  }, []);

  // Keep all mounted dashboards, the navbar, and other tabs aligned with the
  // durable browser preference.
  useEffect(() => {
    const syncPreference = (event) => {
      const next =
        event.type === "storage"
          ? event.newValue === "true"
          : event.detail?.enabled === true;
      enabledRef.current = next;
      setEnabled(next);
      if (!next) stopRepeat();
    };
    const syncStorage = (event) => {
      if (event.key === ORDER_ALERTS_ENABLED_KEY) syncPreference(event);
    };

    window.addEventListener(ORDER_ALERTS_STATE_EVENT, syncPreference);
    window.addEventListener("storage", syncStorage);
    return () => {
      window.removeEventListener(ORDER_ALERTS_STATE_EVENT, syncPreference);
      window.removeEventListener("storage", syncStorage);
    };
  }, [stopRepeat]);

  return {
    // state
    enabled,
    permission,
    pushState,
    /** The browser refused to play the alert sound — the operator thinks alerts
     *  are on but will hear nothing until they interact with the page. Surface
     *  this; do not treat it as an internal detail. */
    audioBlocked,
    // actions
    notify,
    /** Call from a click handler to satisfy the autoplay policy. */
    unlockAudio,
    play,          // low-level (compat with old direct play() call sites)
    toggle,
    enable,
    disable,
    acknowledge,
    enablePush,
    disablePush,
  };
}
